/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import libraries */
import type { Api2ValueDataItem as Api2TimeSeriesDataItem } from '@/lib/overview/api2ValueData';

type SortableApi2TimeSeriesDataItemValueKey =
    | 'aggregation'
    | 'formattedValue'
    | 'fractionDigits'
    | 'rawValue'
    | 'unit'
    | 'urn';

interface SortApi2TimeSeriesDataItemsOptions {
    by: number | string;
    direction?: 'asc' | 'desc';
    nulls?: 'first' | 'last';
    value: SortableApi2TimeSeriesDataItemValueKey;
}

function normalizeSortableValue(value: unknown): number | string | null {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === 'number' || typeof value === 'string') {
        return value;
    }

    return String(value);
}

export function sortApi2TimeSeriesDataItems(
    items: Api2TimeSeriesDataItem[],
    {
        by,
        direction = 'asc',
        nulls = 'last',
        value,
    }: SortApi2TimeSeriesDataItemsOptions,
): Api2TimeSeriesDataItem[] {
    const directionFactor = direction === 'desc' ? -1 : 1;
    const nullFactor = nulls === 'first' ? -1 : 1;

    return [...items].sort((leftItem, rightItem) => {
        const leftValue = normalizeSortableValue(leftItem[value].get(by));
        const rightValue = normalizeSortableValue(rightItem[value].get(by));

        if (leftValue === null && rightValue === null) {
            return 0;
        }

        if (leftValue === null) {
            return nullFactor;
        }

        if (rightValue === null) {
            return -nullFactor;
        }

        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
            return (leftValue - rightValue) * directionFactor;
        }

        return String(leftValue).localeCompare(String(rightValue)) * directionFactor;
    });
}
